import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getSessionCookie, getToken, validateSession, validateToken, redirectToPortal, type PortalUser } from '../lib/auth';

/**
 * Authentication Context
 *
 * Provides authentication state to all components in the app.
 * Automatically validates token on mount and redirects if invalid.
 */

interface AuthContextType {
  user: PortalUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkAuth() {
      // DEBUG: Show all cookies available
      console.log('[BESS Auth] ALL cookies:', document.cookie);
      console.log('[BESS Auth] Current domain:', window.location.hostname);

      // Step 1: Try session cookie first (preferred for terralink.cl subdomains)
      const sessionCookie = getSessionCookie();

      console.log('[BESS Auth] Session cookie found:', !!sessionCookie);
      if (sessionCookie) {
        console.log('[BESS Auth] Session cookie value (first 20 chars):', sessionCookie.substring(0, 20) + '...');
      }

      if (sessionCookie) {
        console.log('[BESS Auth] Validating session cookie...');
        const user = await validateSession();

        if (user) {
          console.log('[BESS Auth] Session cookie valid, user:', user.email);
          setUser(user);
          setIsLoading(false);
          return;
        }

        console.log('[BESS Auth] Session cookie invalid, trying token...');
      } else {
        console.log('[BESS Auth] No session cookie found - checking if browser is blocking cross-subdomain cookies');
      }

      // Step 2: Fallback to token-based auth (for non-terralink.cl domains)
      const token = getToken();

      console.log('[BESS Auth] Token found:', !!token);

      if (!token) {
        console.log('[BESS Auth] No token found');
        console.log('[BESS Auth] DEBUGGING MODE: Waiting 30 seconds before redirect...');
        console.log('[BESS Auth] Check the console logs above to see cookie status');

        // Wait 30 seconds to allow debugging
        await new Promise(resolve => setTimeout(resolve, 30000));

        console.log('[BESS Auth] Redirecting to portal...');
        redirectToPortal();
        return;
      }

      // Step 3: Validate token with backend
      console.log('[BESS Auth] Validating token...');
      const validation = await validateToken(token);

      if (!validation || !validation.valid) {
        console.log('[BESS Auth] Token validation failed, redirecting to portal...');
        redirectToPortal();
        return;
      }

      // Step 4: Token is valid, save user info
      console.log('[BESS Auth] Token valid, user:', validation.user.email);
      setUser(validation.user);
      setIsLoading(false);
    }

    checkAuth();
  }, []);

  const logout = () => {
    redirectToPortal();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication state
 *
 * Usage:
 *   const { user, isAuthenticated, logout } = useAuth();
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
